import React from 'react'

import classNames from 'classnames'

import { Box, BoxProps } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import {
    Popup,
    PopupCloseTrigger,
    PopupContent,
    PopupFooter,
    PopupInner,
    PopupTrigger,
} from 'ui/components/Popup'
import { stopPropagation } from 'ui/helpers/utilities'

import { useEditableSectionEditableFieldsState } from './hooks/useEditableSectionEditableFieldsState'
import { useEditableSectionState } from './hooks/useEditableSectionState'
import { SectionAttributeEditor } from './SectionAttributeEditor'

import {
    EditableSectionPopupContentStyle,
    EditableSectionStyles,
    EditableSectionTriggerButtonStyles,
    EditableSectionTriggerContentInnerStyle,
    EditableSectionTriggerContentStyle,
    EditableSectionTriggerStyles,
} from './EditableSection.css'

type EditableSectionProps = BoxProps & {
    fields: FieldDto[]
    buttonSide?: 'top' | 'bottom' | 'left' | 'right'
    wrapInButton?: boolean
}

export const EditableSection: React.FC<EditableSectionProps> = ({
    children,
    fields,
    className: providedClassName,
    buttonSide = 'top',
    wrapInButton = true,
    ...props
}) => {
    const { isOpen, onOpenChange, isEditable, onInteractOutside } = useEditableSectionState()

    const className = classNames(
        EditableSectionStyles.styleFunction({ isEditable }),
        providedClassName
    )

    if (!isEditable) {
        return (
            <Box className={className} {...props}>
                {children}
            </Box>
        )
    }

    return (
        <Box className={className} {...props}>
            <Popup open={isOpen} onOpenChange={onOpenChange} modal={false}>
                <PopupTrigger asChild>
                    {wrapInButton ? (
                        <Box
                            as="button"
                            className={EditableSectionTriggerStyles.styleFunction({ buttonSide })}
                        >
                            <Button
                                size="xs"
                                variant="secondary"
                                startIcon={{ name: 'PenLine' }}
                                role="none"
                                display="block"
                                className={EditableSectionTriggerButtonStyles.styleFunction({
                                    buttonSide,
                                })}
                                aria-expanded={isOpen}
                                tabIndex={-1}
                            />
                            <Box className={EditableSectionTriggerContentStyle}>
                                <Box
                                    className={EditableSectionTriggerContentInnerStyle}
                                    pointerEvents={isOpen ? 'none' : undefined}
                                >
                                    {children}
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        children
                    )}
                </PopupTrigger>
                <PopupContent
                    align="start"
                    side="bottom"
                    className={EditableSectionPopupContentStyle}
                    onCloseAutoFocus={stopPropagation}
                    onInteractOutside={onInteractOutside}
                >
                    {isOpen && <EditableFields fields={fields} onOpenChange={onOpenChange} />}
                </PopupContent>
            </Popup>
        </Box>
    )
}

type EditableFieldsProps = {
    fields: FieldDto[]
    onOpenChange: (open: boolean) => void
}

export const EditableFields: React.FC<EditableFieldsProps> = ({ fields, onOpenChange }) => {
    const { editableFields, tempRecord, onChangeAttribute, handleSubmit, isDirty, handleKeyDown } =
        useEditableSectionEditableFieldsState({ fields, onOpenChange })

    return (
        <Box
            as="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            noValidate
            maxHeight="full"
            display="grid"
            overflow="hidden"
            style={{
                gridTemplateRows: '1fr auto',
            }}
        >
            <PopupInner flex column gap="m" maxHeight="full" overflowY="auto">
                {editableFields.map((field) => (
                    <SectionAttributeEditor
                        key={field.api_name}
                        field={field}
                        value={tempRecord[field.api_name]}
                        onChange={(value) => onChangeAttribute(field.api_name, value)}
                        isReadOnly={field.isReadOnly}
                        record={tempRecord}
                    />
                ))}
            </PopupInner>
            <PopupFooter layout="right">
                <PopupCloseTrigger>
                    <Button variant="ghost" size="s">
                        Cancel
                    </Button>
                </PopupCloseTrigger>
                <Button type="submit" variant="primary" size="s" disabled={!isDirty}>
                    Save changes
                </Button>
            </PopupFooter>
        </Box>
    )
}
